import React from "react";
import { Slider } from "@mui/material";

interface DynamicSelectorProps<T> {
  title: string;
  type: string;
  values: T[];
  labels: string[];
  name: string;
  selectedValue: T;
  onChange: (value: T) => void;
  additionalProps?: { [key: string]: any }; // Add this line
}

function DynamicSelector<T extends string | number>({
  title,
  type,
  values,
  labels,
  name,
  selectedValue,
  onChange,
  additionalProps = {}

}: DynamicSelectorProps<T>) {
  
    if (type === "Radio") {
        // Check if there are more than 3 options to determine if we should use a grid layout
        const useGridLayout = values.length > 3;
        
        return (
            <div className="filter-section">
              <span className="filter-section-title">{title}</span>
              
              {useGridLayout ? (
                // Grid layout for many options (like SSP)
                <div className="grid-radio-group">
                  {values.map((value, index) => (
                    <div 
                      key={value} 
                      className="radio-option"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="radio"
                        id={`${name}-${value}`}
                        name={name}
                        value={value}
                        checked={selectedValue === value}
                        onChange={(e) => {
                          e.stopPropagation();
                          onChange(value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        {...additionalProps}
                      />
                      <label htmlFor={`${name}-${value}`} onClick={(e) => e.stopPropagation()}>{labels[index]}</label>
                    </div>
                  ))}
                </div>
              ) : (
                // Horizontal layout for few options (like Total/Per Capita)
                <div className="radio-group">
                  {values.map((value, index) => (
                    <div key={value} className="radio-option">
                      <input
                        type="radio"
                        id={`${name}-${value}`}
                        name={name}
                        value={value}
                        checked={selectedValue === value}
                        onChange={(e) => {
                          // Create a new event that won't propagate to parent components
                          e.stopPropagation();
                          onChange(value);
                        }}
                        
                        {...additionalProps} // Spread additionalProps here
                      />
                      <label htmlFor={`${name}-${value}`}>{labels[index]}</label>
                    </div>
                  ))}
                </div>
              )}
            </div>
        );
    }
    else if (type === "Dropdown") {
        return (
            <div className="filter-section">
              <span className="filter-section-title">{title}</span>
              <div className="selector-dropdown">
                <select
                  value={selectedValue}
                  onChange={(e) => onChange(e.target.value as T)}
                  {...additionalProps} // Spread additionalProps here
                >
                  {values.map((value, index) => (
                    <option key={value} value={value}>
                      {labels[index]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
        );
    }
    else if (type === "Slider") {
      const currentIndex = values.indexOf(selectedValue);
      const safeIndex = currentIndex !== -1 ? currentIndex : 0;
      const percentage = values.length > 1 ? (safeIndex / (values.length - 1)) * 100 : 0;
      
      return (
          <div className="filter-section">
            <span className="filter-section-title">{title}</span>
            <div className="mui-style-slider-container">
              <input
                type="range"
                className="mui-style-slider-input"
                aria-label={title}
                name={name}
                min={0}
                max={values.length - 1}
                step={1}
                value={safeIndex}
                onChange={(e) => {
                  const newIndex = parseInt(e.target.value);
                  if (newIndex >= 0 && newIndex < values.length) {
                    onChange(values[newIndex]);
                  }
                }}
                {...additionalProps}
              />
              
              <div className="mui-style-slider-track-container">
                <div className="mui-style-slider-rail" />
                <div 
                  className="mui-style-slider-track" 
                  style={{ width: `${percentage}%` }}
                />
                
                {/* Marks with labels */}
                {values.map((_, index) => {
                  const markPercentage = values.length > 1 ? (index / (values.length - 1)) * 100 : 0;
                  const isFirst = index === 0;
                  const isLast = index === values.length - 1;
                  
                  return (
                    <React.Fragment key={index}>
                      <div
                        className={`mui-style-slider-mark ${index <= safeIndex ? 'active' : ''}`}
                        style={{ left: `${markPercentage}%` }}
                      />
                      <div
                        className={`mui-style-slider-mark-label ${isFirst ? 'first' : ''} ${isLast ? 'last' : ''}`}
                        style={{ left: `${markPercentage}%` }}
                      >
                        {labels[index]}
                      </div>
                    </React.Fragment>
                  );
                })}
                
                {/* Thumb with value label */}
                <div 
                  className="mui-style-slider-thumb"
                  style={{ left: `${percentage}%` }}
                >
                  <div className="mui-style-slider-value-label">
                    {labels[safeIndex]}
                  </div>
                </div>
              </div>
            </div>
          </div>
      );
  }
    
    // Default fallback
    return null;
}

export default DynamicSelector;